import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ComboboxComponent } from '@appkit4/angular-components/combobox';
import { fieldsValidators } from '@shared/constants/field-name-validators';
import { notEmptyObjectValidator } from '@shared/validators/not-empty-object';
import {
  Collection,
  EntityType,
  GridDrawerComponent,
  ResidencyJurisdiction,
} from 'src/app/modules/admin/models';
import {
  EntityTypeService,
  ResidencyJurisdictionService,
} from 'src/app/modules/admin/services';
import { Entity } from 'src/app/modules/portfolio/models/entity';

@Component({
  standalone: false,
  selector: 'gstar-upsert-entity',
  templateUrl: './upsert-entity.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpsertEntityComponent
  extends GridDrawerComponent<Entity>
  implements OnInit, OnDestroy
{
  private fb = inject(FormBuilder);
  private residencyJurisdictionService = inject(ResidencyJurisdictionService);
  private entityTypeService = inject(EntityTypeService);

  public readonly maxNameLength = 255;

  get residencyJurisdictionValue(): ResidencyJurisdiction | null | undefined {
    return this.entityForm.get('residencyJurisdiction')?.value;
  }

  get entityTypeValue(): EntityType | null | undefined {
    return this.entityForm.get('entityType')?.value;
  }

  get disableEntityType(): boolean {
    return !this.residencyJurisdictionValue?.country;
  }

  public entityForm: FormGroup = this.fb.group({
    name: [
      '',
      [
        Validators.required,
        Validators.maxLength(this.maxNameLength),
        Validators.pattern(fieldsValidators.textBoxValue),
      ],
    ],
    residencyJurisdiction: [{}, [notEmptyObjectValidator()]],
    entityType: [{}, [notEmptyObjectValidator()]],
    isActive: true,
  });

  destroyRef = inject(DestroyRef);

  highlightText!: string;

  get nameField(): AbstractControl<string> {
    return this.entityForm.get('name')!;
  }

  @Output() create = new EventEmitter<Entity>();
  @Output() update = new EventEmitter<Entity>();
  @Output() updateValues = new EventEmitter<FormGroup>();

  @ViewChild('residencyJurisdictionCombobox', { static: true })
  residencyJurisdictionCombobox!: ComboboxComponent;

  @ViewChild('entityTypeCombobox', { static: true })
  entityTypeCombobox!: ComboboxComponent;

  hasSelectedJurisdiction: WritableSignal<boolean> = signal<boolean>(false);

  ngOnInit(): void {
    if (this.isEdit) {
      this.residencyJurisdictionSubscribe();
      this.entityTypeSubscribe();
    }
    this.formValueChanges();
    this.prepopulateForm();
  }

  ngOnDestroy(): void {
    this.resetForm();
  }

  formValueChanges(): void {
    this.entityForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(values => {
        if (this.entityForm.valid) {
          this.updateValues.emit(this.entityForm);
        }
        if (this.entityForm.dirty || this.entityForm.touched) {
          this.modelChange.emit({
            model: {
              ...this.model,
              ...values,
            },
            prevModel: this.model!,
            isValid: this.entityForm.valid,
          });
        }
      });
  }

  prepopulateForm(): void {
    if (this.model) {
      this.entityForm.setValue({
        name: this.model?.name,
        residencyJurisdiction: this.model?.residencyJurisdiction,
        entityType: this.model?.entityType,
        isActive: this.model?.isActive,
      });
    }
  }

  selectResidencyJurisdiction(
    residencyJurisdiction: ResidencyJurisdiction | null | undefined
  ): void {
    this.hasSelectedJurisdiction.set(true);
    this.entityForm
      .get('residencyJurisdiction')!
      .setValue(residencyJurisdiction);
  }

  selectEntityType(entityType: EntityType | null | undefined): void {
    this.entityForm.get('entityType')!.setValue(entityType);
  }

  resetForm(): void {
    this.entityForm.patchValue({
      name: '',
      residencyJurisdiction: null,
      entityType: null,
      isActive: true,
    });
  }

  residencyJurisdictionSubscribe(): void {
    this.residencyJurisdictionService
      .get()
      .subscribe((collection: Collection<ResidencyJurisdiction>) => {
        const residencyJurisdiction = collection.items;
        const selectedJurisdiction = residencyJurisdiction.find(
          (jur: ResidencyJurisdiction) =>
            jur.residencyJurisdictionId ===
            this.entityForm.value.residencyJurisdiction.residencyJurisdictionId
        );
        this.entityForm.controls['residencyJurisdiction'].setValue(
          selectedJurisdiction
        );
        if (selectedJurisdiction)
          this.residencyJurisdictionService.selectResidencyJurisdiction(
            selectedJurisdiction
          );
      });
  }

  entityTypeSubscribe(): void {
    this.entityTypeService
      .get()
      .subscribe((collection: Collection<EntityType>) => {
        const entityType: EntityType[] = collection.items;
        this.entityForm.controls['entityType'].setValue(
          entityType.find(
            (eType: EntityType) =>
              eType.entityTypeId ===
              this.entityForm.value.entityType.entityTypeId
          )
        );
      });
  }
}
